import './DocumentField.style.less'

import { Button, Spin, message, Row, Col } from 'antd'
import {
  CloudUploadOutlined,
  DeleteFilled,
  LoadingOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import Dragger from 'antd/lib/upload/Dragger'
import LoadingSpinner from '@/screens/Order/components/LoadingComponent/LoadingSpinner'
import HttpRequest from '@/config/axios.config'
import { queryClient } from '@/config/react-query'
import { notificationTaskIdAtom } from '@/jotai/notification.jotai'
import { isEmptyValue } from '@/utils/common'
import { useAtom } from 'jotai'
import { MAX_FILE_SIZE } from '../../TaskTable.transform'
import { dateFormat } from '@/utils/date'
import { MODAL_CONTENT } from '@/constant/common'
import { DATE_FORMAT } from '@/constant/date'

interface Prop {
  taskId: string
  onUpdated?: any
}

export function DocumentField(prop: Prop) {
  const { taskId, onUpdated } = prop
  const [fileList, setfileList] = useState([])

  const { data, isLoading, refetch } = useQuery(['documentQuery', taskId], () =>
    HttpRequest.get(`/documents/tasks/${taskId}`)
  )

  const [notificationTask, setNotificationTask] = useAtom(
    notificationTaskIdAtom
  )

  useEffect(() => {
    if (
      notificationTask.taskId == taskId &&
      !isEmptyValue(notificationTask.documentId)
    )
      return () => {
        setNotificationTask({ taskId: null, documentId: null, commentId: null })
      }
  }, [])

  const _reload = () => {
    setfileList([])
    refetch()
    onUpdated()
  }

  const uploadMutation = useMutation(
    async (payload: any[]) => {
      let _formData = new FormData()
      payload.forEach((item) => _formData.append('files', item))

      return HttpRequest.post('/medias/multiple/documents', _formData).then(
        (resp) => (resp.data?.data ? resp.data?.data : [])
      )
    },
    {
      onSuccess: () => {},
      onError: () => {},
    }
  )

  const saveMutation = useMutation(
    (payload: any[]) => {
      return HttpRequest.post(`/documents/tasks/${taskId}`, [
        ...payload.map((item) => {
          return {
            external_refrence: taskId,
            table: 'documents',
            name: item?.name ?? '',
            url_path: item?.url ?? '',
            size: item?.size ?? 0,
          }
        }),
      ])
    },
    {
      onSuccess: () => _reload(),
      onError: () => {},
    }
  )

  const _onSave = async () => {
    const result = await uploadMutation.mutateAsync(fileList as any[])
    await saveMutation.mutateAsync(result)
    await queryClient.refetchQueries(['GET_COMMENT_COUNT', taskId])
  }

  const _getFiles = (): {
    Name: string
    Type: string
    Path: string
    CreatedBy: string
    CreatedDate: string
    CreatedByName: string
  }[] => {
    return data && data.data && data.data?.data && data.data?.data?.length
      ? data.data?.data?.map((item) => {
          return {
            Name: item?.name ?? '',
            Type: 'image/jpeg',
            Path: item?.url_path ?? '',
            CreatedBy: item?.created_by?.id ?? '',
            CreatedDate: item?.created_date ?? '',
            CreatedByName: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
          }
        })
      : []
  }

  const uploadProps = {
    accept:
      '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/*',
    onRemove: (file) => {
      const index = fileList.findIndex((item) => item.uid === file.uid)
      const newFileList = fileList.slice()
      if (index !== -1) {
        newFileList.splice(index, 1)
      }
      setfileList(newFileList)
    },
    onChange: (e) => {
      if (e.fileList.length) {
        window.onbeforeunload = () => 'warning'
      } else {
        window.onbeforeunload = undefined
      }
    },
    beforeUpload: (file) => {
      const isLt20M = file.size <= MAX_FILE_SIZE
      if (!isLt20M) {
        message.warn('Can not upload file that is more than 20mb')
        return false
      }

      if (
        !(
          file.type.startsWith('image/') ||
          file.type !== 'application/msword' ||
          file.type !==
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          file.type !== 'application/vnd.ms-excel' ||
          file.type !==
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type !== 'application/pdf'
        )
      ) {
        message.error(`${file.name} is not an Excel, Word, PDF, or Image file`)
        return false
      }

      setfileList([file])
      return false
    },
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: <DeleteFilled />,
    },
    fileList: fileList,
  }

  return (
    <Spin
      spinning={isLoading || uploadMutation.isLoading || saveMutation.isLoading}
      indicator={<LoadingSpinner />}
    >
      <div className="custom-upload">
        {false ? (
          <div className="text-center mt-3">
            <Spin
              size="small"
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
          </div>
        ) : (
          <div className={`${_getFiles().length ? 'wrap-upload-list' : ''}`}>
            {_getFiles().map((file: any, index) => {
              return (
                <div key={index} className="file-item-container">
                  <Row align={'top'}>
                    <PaperClipOutlined />
                    <div>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-item-name"
                        title={file?.Name}
                        href={file?.Path}
                      >
                        {file?.Name}
                      </a>

                      <div className="upload-by-container">
                        {'By '}
                        <span className="uploader-name">
                          <b>{file?.CreatedByName}</b>
                        </span>
                        {' on '}
                        {dateFormat(file?.CreatedDate, DATE_FORMAT.DDMMYY_FULL)}
                      </div>
                    </div>
                  </Row>
                </div>
              )
            })}
          </div>
        )}
        <Dragger {...uploadProps} className="br-1" style={{ fontWeight: 600 }}>
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p className="ant-upload-text">{MODAL_CONTENT.UPLOAD_FILE}</p>
          <p className="ant-upload-hint text-hint-upload">
            {MODAL_CONTENT.UPLOAD_FILE_MAX}
          </p>
        </Dragger>
        <Row gutter={16} className="footer-wrapper">
          <Col span={12}>
            <Button
              key="1"
              disabled={
                uploadMutation.isLoading ||
                saveMutation.isLoading ||
                fileList.length < 1
              }
              className="dialog-cancel-button default-dialog-button"
              onClick={() => setfileList([])}
              block
            >
              Cancel
            </Button>
          </Col>
          <Col span={12}>
            <Button
              key="2"
              type="primary"
              className="default-dialog-button"
              disabled={
                saveMutation.isLoading ||
                uploadMutation.isLoading ||
                fileList.length < 1
              }
              onClick={_onSave}
              block
            >
              Save
            </Button>
          </Col>
        </Row>
      </div>
    </Spin>
  )
}
